import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import PropTypes from "prop-types";

import { fetchCounsellorPortalDashboardBookings } from "../../services/counsellorPortalApi";
import { useToast } from "../../hook/useToast";
import MeetLinkCell from "./MeetLinkCell";
import ReportLinkCell from "./ReportLinkCell";

const DEFAULT_PAGE_SIZE = 10;

const BASE_COLUMNS = [
  { id: "sr", label: "Sr No", align: "left", width: 70 },
  { id: "name", label: "Student Name", align: "left", width: 220 },
  { id: "city", label: "City", align: "left", width: 150 },
  { id: "date_time", label: "Date & Time", align: "left", width: 200 },
  { id: "gmail_id", label: "Gmail ID", align: "left", width: 220 },
  { id: "report", label: "Report Link", align: "center", width: 110 },
  { id: "meet", label: "Meet Link", align: "center", width: 90 },
  { id: "status", label: "Status", align: "center", width: 110 },
  { id: "completion", label: "Completion", align: "center", width: 120 },
];

const BOOKING_MONTH_COLUMN = {
  id: "booking_month",
  label: "Booking Month",
  align: "center",
  width: 140,
};

const STATUS_CHIP_COLORS = {
  pending: { bgcolor: "#FFF6E5", color: "#B54708" },
  confirmed: { bgcolor: "#E6F4EA", color: "#1A7F37" },
  completed: { bgcolor: "#EAF1FF", color: "#1F4FB6" },
  cancelled: { bgcolor: "#FDECEC", color: "#B42318" },
  failed: { bgcolor: "#F4F3FF", color: "#5925DC" },
};

const PERIOD_OPTIONS = [
  { value: "", label: "All" },
  { value: "today", label: "Bookings for Today" },
  { value: "week", label: "Bookings for this Week" },
  { value: "month", label: "Bookings for this Month" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "failed", label: "Failed" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
};

const getInitials = (name = "") => {
  const trimmed = String(name).trim();
  if (!trimmed) return "?";

  const [first, second] = trimmed.split(/\s+/);
  const initials = `${first?.[0] || ""}${second?.[0] || ""}`.toUpperCase();
  return initials || "?";
};

const getStatusChipColors = (status) =>
  STATUS_CHIP_COLORS[(status || "").toLowerCase()] || {
    bgcolor: "#F1F2F4",
    color: "#475467",
  };

const formatStatusLabel = (status) => {
  const normalized = String(status || "").trim();
  if (!normalized) return "--";

  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("en-IN") : "--";
};

const selectSx = {
  height: 38,
  borderRadius: "7px",
  fontSize: 13,
  color: "#2D315F",
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E6EF" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E6EF" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#D7DAE5" },
  "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
};

const filterSelectSx = {
  ...selectSx,
  height: 32,
  width: "100%",
  borderRadius: "8px",
  fontSize: 12,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    color: "#171725",
  },
};

const filterMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      maxHeight: 190,
      borderRadius: "8px",
      border: "1px solid #EFEFF4",
      boxShadow: "0 14px 34px rgba(45, 49, 95, 0.14)",
      "& .MuiMenuItem-root": {
        minHeight: 28,
        fontSize: 12,
        whiteSpace: "normal",
      },
    },
  },
};

const DashboardBookingsTable = ({
  fetchBookings = fetchCounsellorPortalDashboardBookings,
  title = "Bookings for Counseling",
  showBookingMonth = false,
}) => {
  const { showToast } = useToast();
  const requestIdRef = useRef(0);

  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [draftFilters, setDraftFilters] = useState({ period: "", status: "" });
  const [appliedFilters, setAppliedFilters] = useState({ period: "", status: "" });
  const [filterAnchor, setFilterAnchor] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timezone = useMemo(detectTimezone, []);
  const columns = useMemo(
    () => (showBookingMonth ? [...BASE_COLUMNS, BOOKING_MONTH_COLUMN] : BASE_COLUMNS),
    [showBookingMonth],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);

    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError("");

    fetchBookings({
      page,
      pageSize,
      search: debouncedSearch,
      status: appliedFilters.status,
      period: appliedFilters.period,
      timezone,
    })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;

        const payload = response?.data?.data || {};
        setBookings(Array.isArray(payload.bookings) ? payload.bookings : []);
        setPagination(
          payload.pagination || {
            page,
            page_size: pageSize,
            total_records: 0,
            total_pages: 0,
          }
        );
      })
      .catch((apiError) => {
        if (requestId !== requestIdRef.current) return;

        const apiMessage =
          apiError?.response?.data?.message ||
          apiError?.response?.data?.detail ||
          apiError?.message ||
          "Could not load bookings. Please try again.";

        setError(apiMessage);
        setBookings([]);
        setPagination({
          page,
          page_size: pageSize,
          total_records: 0,
          total_pages: 0,
        });
        showToast(apiMessage, "error");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [page, pageSize, debouncedSearch, appliedFilters, timezone, showToast, fetchBookings]);

  const totalPages = Math.max(1, pagination.total_pages || 1);
  const totalRecords = pagination.total_records || 0;
  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter(Boolean).length,
    [appliedFilters]
  );
  const hasDraftFilters = useMemo(
    () => Object.values(draftFilters).some(Boolean),
    [draftFilters]
  );
  const isFilterOpen = Boolean(filterAnchor);

  const paginationItems = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);

    if (start > 2) items.push("dots-start");
    for (let i = start; i <= end; i += 1) items.push(i);
    if (end < totalPages - 1) items.push("dots-end");

    items.push(totalPages);
    return items;
  }, [page, totalPages]);

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const handleOpenFilter = (event) => {
    setDraftFilters(appliedFilters);
    setFilterAnchor(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setDraftFilters(appliedFilters);
    setFilterAnchor(null);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setFilterAnchor(null);
  };

  const handleClearFilters = () => {
    const empty = { period: "", status: "" };
    setDraftFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
    setFilterAnchor(null);
  };

  const renderFilterSelect = (key, label, placeholder, options) => (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#4A4F63", mb: 0.45 }}>
        {label}
      </Typography>
      <Select
        fullWidth
        displayEmpty
        value={draftFilters[key]}
        IconComponent={KeyboardArrowDownIcon}
        onChange={(event) => handleFilterChange(key, event.target.value)}
        renderValue={(value) => {
          if (!value) {
            return (
              <Typography sx={{ fontSize: 12, color: "#92929D" }}>
                {placeholder}
              </Typography>
            );
          }
          return options.find((option) => option.value === value)?.label || value;
        }}
        MenuProps={filterMenuProps}
        sx={filterSelectSx}
      >
        {options.map((option) => (
          <MenuItem key={`${key}-${option.value}`} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        bgcolor: "#fff",
        border: "1px solid #EFEFF4",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          minHeight: 58,
          px: 1.5,
          py: 1.2,
          display: "flex",
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          borderBottom: "1px solid #EFEFF4",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#171725" }}>
            {title}
          </Typography>
          <Chip
            label={formatNumber(totalRecords)}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: "#F0F0F7",
              color: "#6B7280",
              borderRadius: "5px",
            }}
          />
          {activeFilterCount > 0 ? (
            <Chip
              label={`${activeFilterCount} filters`}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: "#FFF4E5",
                color: "#B54708",
                borderRadius: "5px",
              }}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: { xs: "wrap", md: "nowrap" },
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            IconComponent={KeyboardArrowDownIcon}
            sx={{ ...selectSx, width: 110 }}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.9,
              width: { xs: "100%", sm: "auto" },
              minWidth: 0,
            }}
          >
            <TextField
              placeholder="Search name or email..."
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{
                flex: { xs: 1, sm: "0 0 auto" },
                width: { xs: "auto", sm: 280 },
                minWidth: { xs: 0, sm: 240 },
                "& .MuiOutlinedInput-root": {
                  height: 38,
                  borderRadius: "7px",
                  fontSize: 13,
                  bgcolor: "#fff",
                  "& fieldset": { borderColor: "#E4E6EF" },
                  "&:hover fieldset": { borderColor: "#E4E6EF" },
                  "&.Mui-focused fieldset": { borderColor: "#D7DAE5" },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {loading && search.trim() !== debouncedSearch ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SearchIcon sx={{ fontSize: 19, color: "#2D315F" }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <Button
              startIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenFilter}
              sx={{
                height: 38,
                minWidth: 96,
                borderRadius: "7px",
                border: activeFilterCount ? "1px solid #FFA447" : "1px solid #E4E6EF",
                bgcolor: activeFilterCount ? "#FFF7ED" : "#fff",
                color: activeFilterCount ? "#B54708" : "#2D315F",
                fontSize: 13,
                fontWeight: 700,
                textTransform: "none",
                px: 1.4,
                flexShrink: 0,
                "&:hover": {
                  bgcolor: activeFilterCount ? "#FFF2E0" : "#F5F6FA",
                  borderColor: activeFilterCount ? "#FFA447" : "#D7DAE5",
                },
              }}
            >
              Filter
              {activeFilterCount ? (
                <Box
                  component="span"
                  sx={{
                    ml: 0.8,
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    borderRadius: "999px",
                    bgcolor: "#FFA447",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 800,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {activeFilterCount}
                </Box>
              ) : null}
            </Button>
          </Box>
        </Box>
      </Box>

      {isFilterOpen ? (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 10,
          }}
          onClick={handleCloseFilter}
        />
      ) : null}

      {isFilterOpen ? (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            right: { xs: 16, md: 32 },
            mt: 1,
            width: 278,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: "10px",
            border: "1px solid #EFEFF4",
            boxShadow: "0 18px 48px rgba(45, 49, 95, 0.16)",
            overflow: "hidden",
            zIndex: 11,
          }}
        >
          <Box
            sx={{
              px: 1.4,
              py: 1.05,
              borderBottom: "1px solid #EFEFF4",
              bgcolor: "#FAFBFD",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#171725" }}>
              Filter Bookings
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#92929D", mt: 0.15 }}>
              Choose period or status and apply
            </Typography>
          </Box>

          <Box sx={{ p: 1.4, display: "grid", gap: 0.9 }}>
            {renderFilterSelect("period", "Period", "Select period", PERIOD_OPTIONS)}
            {renderFilterSelect("status", "Status", "Select status", STATUS_OPTIONS)}
          </Box>

          <Box
            sx={{
              px: 1.4,
              py: 1.05,
              display: "flex",
              justifyContent: "flex-end",
              gap: 0.8,
              borderTop: "1px solid #EFEFF4",
              bgcolor: "#fff",
            }}
          >
            <Button
              onClick={handleClearFilters}
              disabled={loading || (!activeFilterCount && !hasDraftFilters)}
              sx={{
                height: 30,
                borderRadius: "7px",
                color: "#2D315F",
                border: "1px solid #E4E6EF",
                bgcolor: "#fff",
                textTransform: "none",
                fontSize: 12,
                px: 1.2,
                "&:hover": { bgcolor: "#F5F6FA", borderColor: "#D7DAE5" },
              }}
            >
              Clear
            </Button>

            <Button
              onClick={handleApplyFilters}
              disabled={loading}
              sx={{
                height: 30,
                borderRadius: "7px",
                bgcolor: "#2D315F",
                color: "#fff",
                textTransform: "none",
                fontSize: 12,
                px: 1.35,
                "&:hover": { bgcolor: "#252957" },
                "&.Mui-disabled": { bgcolor: "#D5D7E2", color: "#fff" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Paper>
      ) : null}

      {error ? (
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography sx={{ fontSize: 13, color: "#B42318" }}>{error}</Typography>
        </Box>
      ) : null}

      <TableContainer
        sx={{
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-track": { bgcolor: "#F2F3F7" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#C6CAD8",
            borderRadius: 20,
          },
        }}
      >
        <Table sx={{ minWidth: showBookingMonth ? 1310 : 1170, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#FAFBFD" }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    width: column.width,
                    maxWidth: column.width,
                    height: 45,
                    py: 0,
                    pl: column.align === "left" ? 2.5 : 1.5,
                    pr: 1.5,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6B7280",
                    borderBottom: "1px solid #EFEFF4",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : bookings.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 8, color: "#92929D", fontSize: 14 }}
                >
                  {debouncedSearch || activeFilterCount
                    ? "No bookings match your search or filters"
                    : "No bookings found"}
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => {
                const name = booking.student_name || "Unnamed Student";
                const status = (booking.status || "").toLowerCase();
                const chipStyles = getStatusChipColors(status);
                const completionStatus = (
                  booking.completion_status || "pending"
                ).toLowerCase();
                const completionChipStyles = getStatusChipColors(
                  completionStatus,
                );
                const reportHref = booking.report_link || booking.latest_report_link || "";
                const meetHref = booking.google_meet_link || "";

                return (
                  <TableRow
                    key={booking.booking_id}
                    sx={{
                      height: 68,
                      "& td": { borderBottom: "1px solid #EFEFF4" },
                      "&:hover": { bgcolor: "#FCFCFE" },
                    }}
                  >
                    <TableCell sx={{ pl: 3, fontSize: 13, color: "#111827" }}>
                      {booking.sr_no ?? "—"}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "#EEF2FF",
                            color: "#2D315F",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(name)}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#171725",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {name}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        fontSize: 13,
                        color: "#171725",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {booking.city || "—"}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontSize: 13,
                        color: "#4A4F63",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {booking.date_time || "—"}
                    </TableCell>

                    <TableCell
                      title={booking.gmail_id || ""}
                      sx={{
                        fontSize: 13,
                        color: "#4A4F63",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {booking.gmail_id || "—"}
                    </TableCell>

                    <TableCell align="center">
                      <ReportLinkCell href={reportHref} studentName={name} />
                    </TableCell>

                    <TableCell align="center">
                      <MeetLinkCell href={meetHref} />
                    </TableCell>

                    <TableCell align="center">
                      {status ? (
                        <Chip
                          label={formatStatusLabel(status)}
                          size="small"
                          sx={{
                            bgcolor: chipStyles.bgcolor,
                            color: chipStyles.color,
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: "6px",
                            textTransform: "capitalize",
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 13, color: "#98A2B3" }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={formatStatusLabel(completionStatus)}
                        size="small"
                        sx={{
                          bgcolor: completionChipStyles.bgcolor,
                          color: completionChipStyles.color,
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: "6px",
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>

                    {showBookingMonth ? (
                      <TableCell align="center">
                        <Chip
                          label={booking.booking_month || "--"}
                          size="small"
                          sx={{
                            bgcolor: "#F1F2F4",
                            color: "#475467",
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: "6px",
                          }}
                        />
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          minHeight: 54,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 1,
          bgcolor: "#fff",
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#92929D" }}>
          {totalRecords === 0
            ? "Total - 0"
            : `Total - ${formatNumber(totalRecords)}`}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={page === 1 || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <KeyboardArrowLeftIcon
              sx={{ fontSize: 20, color: page === 1 ? "#B8BBC8" : "#2D315F" }}
            />
          </IconButton>

          {paginationItems.map((item, index) => {
            if (String(item).startsWith("dots")) {
              return (
                <Typography
                  key={`${item}-${index}`}
                  sx={{ fontSize: 14, color: "#171725", px: 0.5 }}
                >
                  ...
                </Typography>
              );
            }

            const selected = item === page;

            return (
              <Box
                key={item}
                onClick={() => {
                  if (!loading) setPage(item);
                }}
                sx={{
                  width: selected ? 30 : 26,
                  height: selected ? 30 : 26,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: selected ? "#2D315F" : "transparent",
                  color: selected ? "#fff" : "#171725",
                  fontSize: 13,
                  fontWeight: selected ? 600 : 400,
                  cursor: loading ? "default" : "pointer",
                }}
              >
                {item}
              </Box>
            );
          })}

          <IconButton
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            <KeyboardArrowRightIcon
              sx={{
                fontSize: 20,
                color: page >= totalPages ? "#B8BBC8" : "#2D315F",
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

DashboardBookingsTable.propTypes = {
  fetchBookings: PropTypes.func,
  title: PropTypes.string,
  showBookingMonth: PropTypes.bool,
};

export default DashboardBookingsTable;
